
import { useState } from "react";
import { Group } from "@/lib/types";
import { addGroup, updateGroup, deleteGroup, fetchGroups } from "@/lib/storage/group/api";
import { fetchProducts } from "@/lib/storage/product/api";
import { useToast } from "@/hooks/use-toast";
import { ProductQuantity } from "@/components/group/GroupProductQuantities";
import { supabase } from "@/integrations/supabase/client";

interface UseGroupActionsProps {
  setGroups: (groups: Group[]) => void;
  setProducts: (products: any[]) => void;
}

export const useGroupActions = ({ setGroups, setProducts }: UseGroupActionsProps) => {
  const [showAddGroupForm, setShowAddGroupForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | undefined>(undefined);
  const { toast } = useToast();

  const refreshData = async () => {
    // Refresh groups and products after changes
    const updatedGroups = await fetchGroups(true);
    const updatedProducts = await fetchProducts(true);
    
    setGroups(updatedGroups);
    setProducts(updatedProducts);
  };

  const saveProductQuantities = async (groupId: string, quantities: ProductQuantity[]) => {
    if (quantities.length === 0) return;
    
    try {
      console.log("Saving quantities for group:", groupId, quantities);
      
      // First delete any existing quantities
      await supabase
        .from('group_product_quantities')
        .delete()
        .eq('group_id', groupId);
      
      // Then insert new quantities
      const dbQuantities = quantities.map(q => ({
        group_id: groupId,
        product_id: q.productId,
        quantity: q.quantity
      }));
      
      const { error } = await supabase
        .from('group_product_quantities')
        .insert(dbQuantities);
        
      if (error) throw error;
      
      console.log("Quantities saved successfully");
    } catch (err) {
      console.error("Error saving product quantities:", err);
      // Continue anyway, the group will work with default quantities
    }
  };

  const handleGroupSaved = async (group: Group, productQuantities: ProductQuantity[]) => {
    try {
      const isEditing = !!editingGroup;
      
      if (isEditing) {
        await updateGroup(group);
        await saveProductQuantities(group.id, productQuantities);
        
        toast({
          title: "Group Updated",
          description: `"${group.name}" has been updated.`,
        });
      } else {
        await addGroup(group);
        await saveProductQuantities(group.id, productQuantities);
        
        toast({
          title: "Group Created",
          description: `"${group.name}" has been created.`,
        });
      }
      
      setShowAddGroupForm(false);
      setEditingGroup(undefined);
      
      await refreshData();
    } catch (error: any) {
      console.error("Error saving group:", error);
      
      toast({
        title: "Error",
        description: `Failed to ${editingGroup ? "update" : "create"} group: ${error.message || error}`,
        variant: "destructive",
      });
    }
  };

  const handleEditGroup = (group: Group) => {
    console.log("Editing group:", group);
    setEditingGroup(group);
    setShowAddGroupForm(true);
  };

  const handleDeleteGroup = async (groupId: string) => {
    try {
      await deleteGroup(groupId);
      
      // Also clean up quantities table
      await supabase
        .from('group_product_quantities')
        .delete()
        .eq('group_id', groupId);
      
      toast({
        title: "Group Deleted",
        description: "The group has been deleted.",
      });
      
      await refreshData();
    } catch (error: any) {
      console.error("Error deleting group:", error);
      
      toast({
        title: "Error",
        description: `Failed to delete group: ${error.message || error}`,
        variant: "destructive",
      });
    }
  };

  return {
    showAddGroupForm,
    setShowAddGroupForm,
    editingGroup,
    setEditingGroup,
    handleGroupSaved,
    handleEditGroup,
    handleDeleteGroup,
  };
};
